// task-events-seen.js — per-task "last seen" marker + unread math for the Notifications
// tab (TPT18). Kept pure/standalone (no DOM, no task-detail.js imports) so it is unit
// testable the same way lib/task-app-link.js and lib/task-draft.js are.
//
// Key convention: `tt-task-events-seen:${projectId}:${taskKey}` — follows this app's
// `tt-`/`tt_` prefix convention (lib/task-draft.js's `tt-new-task-draft:2`,
// lib/task-app-link.js's `tt_task_app_url`), NOT the Task App client's `tc-seen-<id>`
// (that's a different codebase/localStorage origin entirely).
//
// The stored value is the highest-seen task_events.id (a string), never a timestamp —
// same choice the Task App's comment-seen marker makes, since ids sort exactly like
// `ORDER BY id DESC` already does server-side.

export function eventSeenKey(projectId, taskKey) {
  return `tt-task-events-seen:${projectId}:${taskKey}`;
}

export function getSeenEventId(projectId, taskKey) {
  try {
    const stored = Number(localStorage.getItem(eventSeenKey(projectId, taskKey)));
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
  } catch {
    return 0;
  }
}

// Monotonic — a stale/out-of-order write (e.g. a slower earlier request resolving after
// a newer one already advanced the marker) can never move it backwards.
export function setSeenEventId(projectId, taskKey, id) {
  const next = Number(id);
  if (!Number.isFinite(next) || next <= 0) return;
  const current = getSeenEventId(projectId, taskKey);
  if (next <= current) return;
  try {
    localStorage.setItem(eventSeenKey(projectId, taskKey), String(Math.floor(next)));
  } catch {
    // localStorage may be unavailable (private mode, quota) — badge just won't persist
  }
}

function actorId(event) {
  if (event && event.actor && event.actor.id != null) return Number(event.actor.id);
  if (event && event.actor_user_id != null) return Number(event.actor_user_id);
  return null;
}

// Unread = id greater than the seen marker AND authored by someone other than the
// viewer — the viewer's own events (a comment they just posted, a status change they
// just made) never bump their own badge. A system/null-actor event (actorId === null)
// still counts as unread, since it can't possibly be "the viewer's own".
export function unreadEventIds(events, seenId, currentUserId) {
  const viewerId = currentUserId != null ? Number(currentUserId) : null;
  return (events || [])
    .filter((e) => e && e.id > seenId && actorId(e) !== viewerId)
    .map((e) => e.id);
}

export function maxEventId(events) {
  let max = 0;
  for (const e of events || []) {
    if (e && e.id > max) max = e.id;
  }
  return max;
}
